/**
 * The signature editor — Settings › Customers › Email signatures (Aaron,
 * Oct 24, note 2.7: "Should be able to be done in WYSIWIG and HTML. Code
 * Two's designer would be a good place to pull from").
 *
 * **Visual** is the page itself, editable: `contentEditable` with
 * `document.execCommand`, the note editor's approach and for its reason — and
 * one more. A signature is a table with inline styles and a logo; TipTap, as
 * this app carries it (StarterKit only — no extension for tables, images or text
 * styles), would keep none of that, so the first save from the visual
 * editor would flatten every signature anybody pasted. A browser's own
 * editing keeps the HTML it was given and changes only what is touched.
 *
 * **Merge fields are chips** in Visual — *Full name*, *Branch phone* — which
 * a caret steps over whole, and `{{person.name}}` in HTML. The two convert
 * at the edge (`toEditable` / `fromEditable`): a chip is a `span` that says
 * which field it is, the logo field an `img` drawn with today's logo, a style
 * built on the brand colour drawn in today's colour and put back as the field
 * if nobody restyled it. Everything that leaves goes through
 * `sanitizeSignatureHtml`, here and again on the server.
 *
 * **HTML** is the source, for the person who has a designer's signature to
 * paste; *Insert field* puts the field in at the caret either way.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import {
  SIGNATURE_FIELDS, fieldToken, sanitizeSignatureHtml, signatureField,
  type SignatureEditorMode, type SignatureField, type SignatureValues,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { uploadSignatureImage } from '../lib/api.js'
import { Popover } from '../components/overlays.js'
import { Button } from '../components/primitives.js'
import { TextInput } from '../components/form/index.js'
import {
  IconBold, IconChevronDown, IconImage, IconItalic, IconLink, IconPlus, IconTextColor, IconUpload,
} from '../frame/icons.js'

const TOKEN = /\{\{\s*([a-z]+(?:\.[a-z_]+)?)\s*\}\}/g
const TOKEN_ONLY = /^\{\{\s*([a-z]+(?:\.[a-z_]+)?)\s*\}\}$/

/** A chip, as the canvas draws it. Its classes never leave: `fromEditable` turns it back into the field. */
const CHIP = 'rounded-[5px] bg-accent-soft px-1 py-px text-[0.92em] font-semibold text-accent-dark'

/** Fills a style's fields with today's values, for drawing. */
const filled = (text: string, values: SignatureValues): string =>
  text.replace(TOKEN, (whole, key: string) => values[key] ?? whole)

/** The stored HTML as the canvas edits it: chips for fields, today's logo and colour drawn. */
export function toEditable(html: string, values: SignatureValues): string {
  /*
   * Sanitised first: what comes back from HTML mode is whatever was typed, and
   * this is about to become live markup in the page.
   */
  const clean = sanitizeSignatureHtml(html)
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${clean}</body>`, 'text/html')
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const texts: Text[] = []
  for (let node = walker.nextNode(); node; node = walker.nextNode()) texts.push(node as Text)
  for (const text of texts) {
    const value = text.nodeValue ?? ''
    if (!TOKEN.test(value)) { TOKEN.lastIndex = 0; continue }
    TOKEN.lastIndex = 0
    const parts = doc.createDocumentFragment()
    let at = 0
    for (const match of value.matchAll(TOKEN)) {
      const field = signatureField(match[1]!)
      if (!field) continue
      parts.append(value.slice(at, match.index))
      const chip = doc.createElement('span')
      chip.setAttribute('data-field', field.key)
      chip.setAttribute('contenteditable', 'false')
      chip.className = CHIP
      chip.textContent = field.label
      parts.append(chip)
      at = match.index! + match[0].length
    }
    parts.append(value.slice(at))
    text.replaceWith(parts)
  }
  for (const image of [...doc.body.querySelectorAll('img')]) {
    const token = TOKEN_ONLY.exec(image.getAttribute('src') ?? '')
    if (!token) continue
    image.setAttribute('data-field-src', token[1]!)
    image.setAttribute('src', values[token[1]!] ?? '')
  }
  for (const element of [...doc.body.querySelectorAll('[style]')]) {
    const style = element.getAttribute('style') ?? ''
    if (!style.includes('{{')) continue
    const drawn = filled(style, values)
    element.setAttribute('data-sig-style', style)
    element.setAttribute('data-sig-drawn', drawn)
    element.setAttribute('style', drawn)
  }
  return doc.body.innerHTML
}

/** The canvas back into the stored form: fields where the chips were, sanitised. */
export function fromEditable(root: HTMLElement): string {
  const clone = root.cloneNode(true) as HTMLElement
  for (const chip of [...clone.querySelectorAll('span[data-field]')]) {
    chip.replaceWith(clone.ownerDocument.createTextNode(fieldToken(chip.getAttribute('data-field') ?? '')))
  }
  for (const image of [...clone.querySelectorAll('img[data-field-src]')]) {
    image.setAttribute('src', fieldToken(image.getAttribute('data-field-src') ?? ''))
    image.removeAttribute('data-field-src')
  }
  for (const element of [...clone.querySelectorAll('[data-sig-style]')]) {
    /* Restyled since it was drawn: the new style stands, in the colour it shows. */
    if (element.getAttribute('style') === element.getAttribute('data-sig-drawn')) {
      element.setAttribute('style', element.getAttribute('data-sig-style') ?? '')
    }
    element.removeAttribute('data-sig-style')
    element.removeAttribute('data-sig-drawn')
  }
  return sanitizeSignatureHtml(clone.innerHTML)
}

// ---------------------------------------------------------------------------
// The field picker (CodeTwo's "placeholders")
// ---------------------------------------------------------------------------

const GROUPS: SignatureField['group'][] = ['Person', 'Branch', 'Company']

function FieldPicker({ anchor, values, mode, onPick, onClose }: {
  anchor: HTMLElement | null
  values: SignatureValues
  mode: SignatureEditorMode
  onPick: (field: SignatureField) => void
  onClose: () => void
}) {
  /* A colour is a style's, which only HTML can name; the logo goes in as a picture either way. */
  const offered = SIGNATURE_FIELDS.filter((field) => mode === 'html' || field.kind !== 'colour')
  return (
    <Popover open onClose={onClose} anchor={anchor} width={300} maxHeight={420} label="Insert field"
      className="rounded-[12px] py-1">
      <div role="menu" aria-label="Insert field" className="flex flex-col overflow-y-auto">
        {GROUPS.map((group) => (
          <div key={group} className="flex flex-col">
            <p className="m-0 px-3 pb-0.5 pt-1.5 text-label uppercase text-text-faint">{group}</p>
            {offered.filter((field) => field.group === group).map((field) => {
              const value = values[field.key] ?? ''
              return (
                <button key={field.key} type="button" role="menuitem" onClick={() => onPick(field)}
                  className="mx-1 flex h-8 items-center gap-2 rounded-[9px] px-3 text-left hover:bg-hover">
                  <span className="text-[13px] font-semibold text-text">{field.label}</span>
                  <span className="ml-auto truncate pl-2 text-[11.5px] text-text-faint">
                    {field.kind === 'image' ? (value ? 'the logo' : 'no logo yet')
                      : field.kind === 'colour' ? value : value || 'empty for them'}
                  </span>
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </Popover>
  )
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

function Tool({ label, onClick, children, disabled, pressed }: {
  label: string; onClick: () => void; children: ReactNode; disabled?: boolean; pressed?: boolean
}) {
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled} aria-pressed={pressed}
      /* Down, not click: a click would take the caret out of the canvas before the command runs. */
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn('inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-[7px] px-1.5 text-text-body',
        'hover:bg-hover disabled:opacity-40', pressed && 'bg-accent-soft text-accent-dark')}>
      {children}
    </button>
  )
}

const COLOURS = (values: SignatureValues) => [
  { label: 'Ink', value: 'rgb(29, 29, 26)' },
  { label: 'Grey', value: 'rgb(111, 110, 104)' },
  ...(values['company.colour'] ? [{ label: 'Brand colour', value: values['company.colour'] }] : []),
]

export interface SignatureEditorProps {
  mode: SignatureEditorMode
  /** The stored form — sanitised, fields as `{{…}}`. */
  html: string
  onChange: (html: string) => void
  /** The preview person's fields: what the chips' picker shows and the logo drawn. */
  values: SignatureValues
  disabled?: boolean
}

export function SignatureEditor({ mode, html, onChange, values, disabled = false }: SignatureEditorProps) {
  const [picking, setPicking] = useState(false)
  const [colouring, setColouring] = useState(false)
  const [imaging, setImaging] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const fieldAt = useRef<HTMLButtonElement>(null)
  const colourAt = useRef<HTMLButtonElement>(null)
  const imageAt = useRef<HTMLButtonElement>(null)
  const canvas = useRef<HTMLDivElement>(null)
  const saved = useRef<Range | null>(null)
  const emitted = useRef<string | null>(null)
  const caret = useRef<[number, number]>([html.length, html.length])

  /*
   * Written into the canvas rather than bound to it — a `contentEditable`
   * React controls loses the caret on every keystroke. The value flows out on
   * input and back in only when it changed underneath: another signature
   * opened, a template picked, HTML mode handing back.
   */
  useLayoutEffect(() => {
    const node = canvas.current
    if (mode !== 'visual' || !node) return
    /* A canvas just mounted (coming back from HTML) is empty whatever was last emitted. */
    if (emitted.current === html && node.getAttribute('data-loaded') === 'true') return
    node.innerHTML = toEditable(html, values)
    node.setAttribute('data-loaded', 'true')
    emitted.current = html
  }, [html, mode, values])

  const emit = () => {
    const node = canvas.current
    if (!node) return
    const next = fromEditable(node)
    emitted.current = next
    onChange(next)
  }

  /* Where the caret was in the canvas, so a picker that took the focus can put it back. */
  useEffect(() => {
    if (mode !== 'visual') return undefined
    const remember = () => {
      const selection = document.getSelection()
      const node = canvas.current
      if (!selection || selection.rangeCount === 0 || !node) return
      const range = selection.getRangeAt(0)
      if (node.contains(range.commonAncestorContainer)) saved.current = range.cloneRange()
    }
    document.addEventListener('selectionchange', remember)
    return () => document.removeEventListener('selectionchange', remember)
  }, [mode])

  /** The caret back in the canvas: where it was, else at the end. */
  const restore = (): Range | null => {
    const node = canvas.current
    if (!node) return null
    node.focus()
    const selection = document.getSelection()
    let range = saved.current
    if (!range || !node.contains(range.commonAncestorContainer)) {
      range = document.createRange()
      range.selectNodeContents(node)
      range.collapse(false)
    }
    selection?.removeAllRanges()
    selection?.addRange(range)
    return range
  }

  /** A node at the caret, the caret after it. Ranges, not `insertHTML`, so a chip stays a chip. */
  const insertNode = (node: Node) => {
    const range = restore()
    if (!range) return
    range.deleteContents()
    range.insertNode(node)
    range.setStartAfter(node)
    range.collapse(true)
    const selection = document.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
    saved.current = range.cloneRange()
    emit()
  }

  const command = (name: string, argument?: string) => {
    restore()
    if (name === 'foreColor') document.execCommand('styleWithCSS', false, 'true')
    document.execCommand(name, false, argument)
    emit()
  }

  /** HTML mode: text at the textarea's caret. */
  const insertText = (text: string) => {
    const [start, end] = caret.current
    const next = html.slice(0, start) + text + html.slice(end)
    caret.current = [start + text.length, start + text.length]
    onChange(next)
  }

  const pick = (field: SignatureField) => {
    setPicking(false)
    if (mode === 'html') {
      insertText(field.kind === 'image'
        ? `<img src="${fieldToken(field.key)}" alt="${fieldToken('company.name')}" width="120">`
        : fieldToken(field.key))
      return
    }
    if (field.kind === 'image') { insertImage(fieldToken(field.key), 120); return }
    const chip = document.createElement('span')
    chip.setAttribute('data-field', field.key)
    chip.setAttribute('contenteditable', 'false')
    chip.className = CHIP
    chip.textContent = field.label
    insertNode(chip)
  }

  const insertImage = (src: string, width: number) => {
    if (mode === 'html') {
      insertText(`<img src="${src}" width="${width}">`)
      return
    }
    const image = document.createElement('img')
    const token = TOKEN_ONLY.exec(src)
    if (token) {
      image.setAttribute('data-field-src', token[1]!)
      image.setAttribute('src', values[token[1]!] ?? '')
      image.setAttribute('alt', fieldToken('company.name'))
    } else {
      image.setAttribute('src', src)
    }
    image.setAttribute('width', String(width))
    insertNode(image)
  }

  const link = () => {
    const answer = window.prompt('Link to — a web address, mailto: or tel:', 'https://')
    if (answer === null) return
    const clean = answer.trim()
    if (!clean) { command('unlink'); return }
    if (!/^(https?:\/\/|mailto:|tel:)/i.test(clean)) {
      setProblem('A link goes to a web address, an email (mailto:) or a phone (tel:).')
      return
    }
    setProblem(null)
    command('createLink', clean)
  }

  return (
    <div className={cn('overflow-hidden rounded-field border border-line bg-surface',
      'focus-within:border-accent-line focus-within:ring-2 focus-within:ring-accent-ring', disabled && 'opacity-60')}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-line-soft bg-surface-alt px-1.5 py-1">
        <button ref={fieldAt} type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()}
          onClick={() => setPicking(true)}
          className="inline-flex h-7 items-center gap-1 rounded-[7px] px-2 text-[12.5px] font-semibold text-accent-dark hover:bg-hover">
          <IconPlus size={14} />Insert field<IconChevronDown size={12} />
        </button>
        {mode === 'visual' && (
          <>
            <span aria-hidden className="mx-1 h-4 w-px bg-line" />
            <Tool label="Bold" disabled={disabled} onClick={() => command('bold')}><IconBold size={15} /></Tool>
            <Tool label="Italic" disabled={disabled} onClick={() => command('italic')}><IconItalic size={15} /></Tool>
            <Tool label="Underline" disabled={disabled} onClick={() => command('underline')}>
              <span className="text-[12.5px] font-semibold underline">U</span>
            </Tool>
            <button ref={colourAt} type="button" aria-label="Text colour" title="Text colour" disabled={disabled}
              onMouseDown={(event) => event.preventDefault()} onClick={() => setColouring(true)}
              className="inline-flex h-7 min-w-7 items-center justify-center rounded-[7px] px-1.5 text-text-body hover:bg-hover">
              <IconTextColor size={15} />
            </button>
            <Tool label="Link" disabled={disabled} onClick={link}><IconLink size={15} /></Tool>
            <button ref={imageAt} type="button" aria-label="Image" title="Image" disabled={disabled}
              onMouseDown={(event) => event.preventDefault()} onClick={() => setImaging(true)}
              className="inline-flex h-7 min-w-7 items-center justify-center rounded-[7px] px-1.5 text-text-body hover:bg-hover">
              <IconImage size={15} />
            </button>
            <Tool label="Clear formatting" disabled={disabled} onClick={() => command('removeFormat')}>
              <span className="text-[12px] font-semibold line-through">T</span>
            </Tool>
          </>
        )}
        {mode === 'html' && (
          <span className="ml-1.5 text-[11.5px] text-text-faint">fields go in as {'{{person.name}}'}</span>
        )}
      </div>

      {mode === 'visual' ? (
        <div
          ref={canvas}
          role="textbox"
          aria-multiline="true"
          aria-label="Signature"
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emit}
          data-signature-canvas
          className={cn('min-h-[150px] px-3.5 py-3 text-[13px] leading-[1.45] text-text outline-none',
            /* An email's defaults, not the app's reset: paragraphs spaced, links underlined, pictures inline. */
            '[&_a]:underline [&_img]:inline [&_img]:max-w-none [&_p]:my-0 [&_table]:border-separate',
            '[&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_h1]:text-[2em] [&_h1]:font-bold',
            '[&_h2]:text-[1.5em] [&_h2]:font-bold [&_h3]:text-[1.17em] [&_h3]:font-bold [&_blockquote]:pl-3')}
        />
      ) : (
        /* The editor's own surface, like the canvas: the box around both is the field. */
        <textarea
          aria-label="Signature HTML"
          value={html}
          rows={12}
          disabled={disabled}
          spellCheck={false}
          onChange={(event) => {
            caret.current = [event.target.selectionStart, event.target.selectionEnd]
            onChange(event.target.value)
          }}
          onSelect={(event) => {
            const box = event.currentTarget
            caret.current = [box.selectionStart, box.selectionEnd]
          }}
          className={cn('block min-h-[220px] w-full resize-y bg-transparent px-3.5 py-3 font-mono text-[12px]',
            'leading-[1.5] text-text outline-none focus-visible:shadow-none')}
        />
      )}
      {problem && <p role="alert" className="m-0 border-t border-line-faint px-3.5 py-2 text-[12px] text-danger">{problem}</p>}

      {picking && (
        <FieldPicker anchor={fieldAt.current} values={values} mode={mode} onPick={pick}
          onClose={() => setPicking(false)} />
      )}
      {colouring && (
        <Popover open onClose={() => setColouring(false)} anchor={colourAt.current} width={200} label="Text colour"
          className="rounded-[12px] py-1">
          <div role="menu" aria-label="Text colour" className="flex flex-col">
            {COLOURS(values).map((colour) => (
              <button key={colour.label} type="button" role="menuitem"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => { setColouring(false); command('foreColor', colour.value) }}
                className="mx-1 flex h-8 items-center gap-2.5 rounded-[9px] px-3 text-left text-[13px] hover:bg-hover">
                <span aria-hidden className="h-3.5 w-3.5 rounded-full border border-line" style={{ background: colour.value }} />
                {colour.label}
              </button>
            ))}
          </div>
        </Popover>
      )}
      {imaging && (
        <ImageMenu anchor={imageAt.current} hasLogo={Boolean(values['company.logo'])}
          onClose={() => setImaging(false)}
          onPick={(src, width) => { setImaging(false); insertImage(src, width) }} />
      )}
    </div>
  )
}

/** **Image**: the company's logo, or one uploaded for signatures — never an address from elsewhere. */
function ImageMenu({ anchor, hasLogo, onPick, onClose }: {
  anchor: HTMLElement | null
  hasLogo: boolean
  onPick: (src: string, width: number) => void
  onClose: () => void
}) {
  const [width, setWidth] = useState('120')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const file = useRef<HTMLInputElement>(null)
  const size = Math.max(16, Math.min(600, Number(width) || 120))

  const upload = async (chosen: File | undefined) => {
    if (!chosen) return
    setBusy(true)
    setProblem(null)
    try {
      const made = await uploadSignatureImage(chosen)
      onPick(made.src, size)
    } catch (cause) {
      setProblem((cause as { message?: string }).message ?? 'That image did not upload.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Popover open onClose={onClose} anchor={anchor} width={280} label="Image" className="rounded-[12px] p-3">
      <div className="flex flex-col gap-2.5">
        <Button disabled={!hasLogo} onClick={() => onPick(fieldToken('company.logo'), size)}>
          <IconImage size={15} />The company’s logo
        </Button>
        {!hasLogo && <span className="text-[11px] text-text-faint">No logo yet — add one under Profile &amp; branding</span>}
        <Button disabled={busy} onClick={() => file.current?.click()}>
          <IconUpload size={15} />{busy ? 'Uploading…' : 'Upload an image…'}
        </Button>
        <input ref={file} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden
          onChange={(event) => { void upload(event.target.files?.[0]); event.target.value = '' }} />
        <label className="flex items-center gap-2 text-[12.5px] text-text-muted">
          Width
          <span className="w-[86px]">
            <TextInput aria-label="Width" inputMode="numeric" value={width} suffix="px"
              onChange={(event) => setWidth(event.target.value.replace(/\D/g, ''))} />
          </span>
        </label>
        {problem && <p role="alert" className="m-0 text-[12px] text-danger">{problem}</p>}
      </div>
    </Popover>
  )
}
